/**
 * Keeps the black hover bubbles (`[data-tooltip]::after`, see the ftgs-*.scss
 * overlays) inside the visible area.
 *
 * Each overlay places its bubble with fixed CSS (above / below / right of the
 * element), which can run past the window edge when the overlay sits near it
 * or the window is small. On hover the bubble's real box is measured and, if
 * it overflows, it is flipped to the opposite side of the element (when that
 * side fits) or else slid back in. The correction goes into the element's
 * --ftgs-tip-dx / --ftgs-tip-dy, which the shared rule in tooltips.scss
 * applies as the bubble's `translate` - on top of the overlay's own
 * `transform`, so no overlay rule changes.
 *
 * In the stereo split view the bounds are the overlay's own half (clipped
 * with overflow: hidden) rather than the window. The correction is in the
 * element's local (unsqueezed) px, so the stereo mirror's attribute sync
 * carries the same values onto the copy, which is laid out identically.
 *
 * Inside a clipping container (`.ftgs-tip-clip` - the stats panel, which
 * scrolls once it is taller than the window, and a scroll box clips
 * everything past its edges) the bounds are that container too, and a
 * bubble wider than it is wrapped to fit through --ftgs-tip-max-w (see
 * tooltips.scss) before it is placed.
 */

const MARGIN = 4;

const px = (v: string) => parseFloat(v) || 0;

/** local -> screen scale of `el` (anamorphic stereo squeezes x by 0.5) */
const scaleOf = (el: HTMLElement, rect: DOMRect) => ({
    x: el.offsetWidth ? rect.width / el.offsetWidth : 1,
    y: el.offsetHeight ? rect.height / el.offsetHeight : 1
});

/**
 * Shift along one axis that keeps [lo, hi] inside [min, max]: flip to the
 * other side of the anchor [alo, ahi] when the bubble lies wholly beyond it
 * and the flipped spot fits, otherwise slide it in.
 */
const fitAxis = (lo: number, hi: number, alo: number, ahi: number, min: number, max: number) => {
    if (lo >= min && hi <= max) {
        return 0;
    }
    const size = hi - lo;
    if (hi <= alo) {
        // above / left of the anchor -> mirror to below / right
        const flipped = ahi + (alo - hi);
        if (flipped + size <= max) {
            return flipped - lo;
        }
    } else if (lo >= ahi) {
        // below / right of the anchor -> mirror to above / left
        const flipped = alo - (lo - ahi) - size;
        if (flipped >= min) {
            return flipped - lo;
        }
    }
    if (size > max - min) {
        // wider than the space: keep the start edge (text begins there)
        return min - lo;
    }
    return lo < min ? min - lo : max - hi;
};

const fit = (el: HTMLElement) => {
    el.style.removeProperty('--ftgs-tip-dx');
    el.style.removeProperty('--ftgs-tip-dy');
    el.style.removeProperty('--ftgs-tip-max-w');

    const cs = getComputedStyle(el, '::after');
    if (cs.content === 'none' || cs.position !== 'absolute') {
        return;
    }

    // wrap to the clipping container's inner width (local px; `cs` is live,
    // so the width read below already reflects the wrap)
    const clip = el.closest('.ftgs-tip-clip') as HTMLElement | null;
    if (clip) {
        const chrome = cs.boxSizing === 'content-box' ?
            px(cs.paddingLeft) + px(cs.paddingRight) + px(cs.borderLeftWidth) + px(cs.borderRightWidth) : 0;
        el.style.setProperty('--ftgs-tip-max-w', `${Math.max(0, clip.clientWidth - 2 * MARGIN - chrome)}px`);
    }

    // the bubble is positioned against its containing block's padding box
    const cb = (getComputedStyle(el).position !== 'static' ? el : el.offsetParent) as HTMLElement | null;
    if (!cb) {
        return;
    }
    const cbRect = cb.getBoundingClientRect();
    const scale = scaleOf(cb, cbRect);

    const m = new DOMMatrix(cs.transform === 'none' ? undefined : cs.transform);
    const contentBox = cs.boxSizing === 'content-box';
    const w = px(cs.width) + (contentBox ? px(cs.paddingLeft) + px(cs.paddingRight) + px(cs.borderLeftWidth) + px(cs.borderRightWidth) : 0);
    const h = px(cs.height) + (contentBox ? px(cs.paddingTop) + px(cs.paddingBottom) + px(cs.borderTopWidth) + px(cs.borderBottomWidth) : 0);

    const left = cbRect.left + (cb.clientLeft + px(cs.left) + m.e) * scale.x;
    const top = cbRect.top + (cb.clientTop + px(cs.top) + m.f) * scale.y;
    const right = left + w * scale.x;
    const bottom = top + h * scale.y;

    const half = el.closest('.ftgs-stereo-half');
    let b = half ? half.getBoundingClientRect() : new DOMRect(0, 0, window.innerWidth, window.innerHeight);
    if (clip) {
        // the container's visible box, as far as it is itself on screen
        const c = clip.getBoundingClientRect();
        const l = Math.max(b.left, c.left);
        const t = Math.max(b.top, c.top);
        b = new DOMRect(l, t, Math.min(b.right, c.right) - l, Math.min(b.bottom, c.bottom) - t);
    }
    const a = el.getBoundingClientRect();

    const dx = fitAxis(left, right, a.left, a.right, b.left + MARGIN, b.right - MARGIN);
    const dy = fitAxis(top, bottom, a.top, a.bottom, b.top + MARGIN, b.bottom - MARGIN);

    if (dx) {
        el.style.setProperty('--ftgs-tip-dx', `${dx / scale.x}px`);
    }
    if (dy) {
        el.style.setProperty('--ftgs-tip-dy', `${dy / scale.y}px`);
    }
};

/**
 * Install once. Capture phase so every hover is seen, including the stereo
 * cursor's synthetic pointerover (ftgs-stereo-cursor.ts). The bubbles fade
 * in after a 0.25 s delay, so the correction lands before they show.
 */
const registerTooltipFit = () => {
    document.addEventListener('pointerover', (event: PointerEvent) => {
        const target = event.target as Element | null;
        const el = target?.closest?.('[data-tooltip]');
        if (el instanceof HTMLElement && !el.classList.contains('ftgs-stereo-mirror')) {
            fit(el);
        }
    }, true);
};

export { registerTooltipFit };
